// ============================================================
// Theoretical-vs-Actual variance math
// ============================================================
// Powers the Variance screen. For each inventory item that's used by
// at least one recipe linked to a menu item with sales in the period:
//
//   theoretical_qty = Σ_recipes (
//                       sales_count_for_recipe.item_id ×
//                       Σ_components (
//                         component.qty / recipe.yield_servings
//                       )
//                     )
//   actual_qty      = baseline.qty + Σ receive_logs - Σ waste_logs - now.on_hand
//   variance_qty    = actual_qty - theoretical_qty
//   variance_pct    = variance_qty / theoretical_qty
//   $variance       = variance_qty × effective_unit_cost
//
// Sign convention:
//   positive variance = used MORE than expected → shrinkage / over-portioning / theft
//   negative variance = used LESS → under-portioning OR sales not all rung up
//
// All quantities are normalized to the inventory item's tracking unit
// (item.unit) so on_hand and recipe components compare apples-to-apples.
// When a recipe component is in the inner pack_unit (1 each of a case-
// tracked item), we convert via pack_size before summing.
// ============================================================

// ---------- Unit conversion helpers --------------------------
//
// Convert a quantity expressed in `componentUnit` to the item's
// tracking unit (item.unit). Three cases:
//   1. componentUnit == item.unit  → pass through unchanged
//   2. componentUnit == item.pack_unit (and pack_size > 0)
//        → divide by pack_size (inner → outer)
//   3. fallback → assume same unit as item.unit (best effort)
//
// Used for both theoretical depletion (recipe components → item unit)
// and the receive/waste actuals (logs already store unit, but it
// might differ from item.unit if the manager logged in inner units).
export function convertToItemUnit(qty, componentUnit, item) {
  if (!item) return Number(qty || 0)
  const cu = (componentUnit || '').toLowerCase().trim()
  const iu = (item.unit       || '').toLowerCase().trim()
  const pu = (item.pack_unit  || '').toLowerCase().trim()
  const packSize = Number(item.pack_size || 1)
  const q = Number(qty || 0)
  if (!cu || cu === iu) return q
  if (pu && cu === pu && packSize > 0) return q / packSize
  return q  // unknown unit — best effort, assume same as item.unit
}

// Effective unit cost in the *item's* tracking unit. (We always
// report cost in the item's unit since that's where on_hand lives.)
// For pack-aware items this equals cost_per_unit (NOT the inner-unit
// cost) — when we apply this to a quantity that's already been
// converted to item.unit via convertToItemUnit, the math works out.
export function unitCostForItem(item) {
  if (!item) return 0
  return Number(item.cost_per_unit || 0)
}

// ---------- Variance computation -----------------------------
//
// Inputs (all already RLS-scoped + period-filtered by the caller):
//   period_start, period_end          — YYYY-MM-DD strings (inclusive)
//   items                             — full active items array (with pack info)
//   recipes                           — recipes joined to recipe_components +
//                                       components.item (pack info needed)
//   item_sales                        — rows where period overlaps the variance
//                                       window. Use full quantity_sold (no
//                                       partial-overlap proration in MVP).
//   on_hand_snapshots                 — most-recent baseline at-or-before
//                                       period_start, per item_id
//   receive_logs                      — receive_logs.logged_at within window
//   waste_logs                        — waste_logs.logged_at within window
//
// Returns:
//   {
//     status: 'ready' | 'no_baseline' | 'no_recipes' | 'no_sales',
//     period: { start, end },
//     rows: [{
//       item_id, item_name, unit,
//       theoretical_qty, actual_qty, variance_qty, variance_pct,
//       theoretical_cost, actual_cost, variance_cost,
//       received_qty, wasted_qty, baseline_qty, current_qty,
//       contributing_recipes: [{ recipe_id, recipe_name,
//                                qty_sold_period, per_serving_qty,
//                                contributed_qty }],
//     }],
//     unlinked_recipe_count, unmatched_sales_count,
//   }
//
export function computeVariance({
  period_start,
  period_end,
  items = [],
  recipes = [],
  item_sales = [],
  on_hand_snapshots = [],
  receive_logs = [],
  waste_logs = [],
}) {
  // Indexes -----------------------------------------------------
  const itemById = new Map()
  for (const it of items) itemById.set(it.id, it)

  // sales_qty_by_menu_item_id: how many of each menu item were sold in window
  const salesByMenuItemId = new Map()
  let unmatched_sales_count = 0
  for (const s of item_sales) {
    if (!s.item_id) { unmatched_sales_count++; continue }
    const prev = salesByMenuItemId.get(s.item_id) || 0
    salesByMenuItemId.set(s.item_id, prev + Number(s.quantity_sold || 0))
  }

  // Most-recent baseline snapshot at/before period_start, per item.
  // Snapshots come in pre-sorted desc by snapshot_at. We bucket by
  // item_id and keep the first one we see (= most recent).
  const baselineByItemId = new Map()
  for (const snap of on_hand_snapshots) {
    if (snap.source !== 'baseline') continue
    if (snap.snapshot_at && snap.snapshot_at.slice(0, 10) > period_start) continue
    if (!baselineByItemId.has(snap.item_id)) {
      baselineByItemId.set(snap.item_id, snap)
    }
  }

  // Aggregate receives + wastes by item, converting to the item's
  // tracking unit (in case logs were in a different unit).
  const receivedByItemId = new Map()
  for (const r of receive_logs) {
    const it = itemById.get(r.item_id)
    if (!it) continue
    const q = convertToItemUnit(r.quantity, r.unit, it)
    receivedByItemId.set(r.item_id, (receivedByItemId.get(r.item_id) || 0) + q)
  }
  const wastedByItemId = new Map()
  for (const w of waste_logs) {
    const it = itemById.get(w.item_id)
    if (!it) continue
    const q = convertToItemUnit(w.quantity, w.unit, it)
    wastedByItemId.set(w.item_id, (wastedByItemId.get(w.item_id) || 0) + q)
  }

  // Walk recipes, accumulate theoretical depletion per inventory item.
  // theoreticalByItemId[item_id] = { qty (in item unit), contributing_recipes }
  const theoreticalByItemId = new Map()
  let unlinked_recipe_count = 0
  for (const r of recipes) {
    if (!r.item_id) { unlinked_recipe_count++; continue }
    const sold = salesByMenuItemId.get(r.item_id) || 0
    if (sold <= 0) continue
    const yieldN = Number(r.yield_servings || 1) || 1
    for (const c of (r.components || [])) {
      const item = itemById.get(c.item_id) || c.item
      if (!item) continue
      const perServingQty = Number(c.quantity || 0) / yieldN
      const perServingInItemUnit = convertToItemUnit(perServingQty, c.unit, item)
      const contributed = sold * perServingInItemUnit
      const bucket = theoreticalByItemId.get(item.id) || {
        qty: 0,
        contributing_recipes: [],
      }
      bucket.qty += contributed
      bucket.contributing_recipes.push({
        recipe_id:        r.id,
        recipe_name:      r.name,
        qty_sold_period:  sold,
        per_serving_qty:  perServingInItemUnit,
        component_unit:   c.unit,
        contributed_qty:  contributed,
      })
      theoreticalByItemId.set(item.id, bucket)
    }
  }

  // Build rows. Anchor on the items that appear in either theoretical
  // OR actual side, so a row that received-but-never-sold also shows
  // up (signal: unused stock).
  const allItemIds = new Set([
    ...theoreticalByItemId.keys(),
    ...baselineByItemId.keys(),
  ])
  const rows = []
  for (const itemId of allItemIds) {
    const item = itemById.get(itemId)
    if (!item) continue
    const baseline_qty   = Number(baselineByItemId.get(itemId)?.quantity || 0)
    const received_qty   = Number(receivedByItemId.get(itemId) || 0)
    const wasted_qty     = Number(wastedByItemId.get(itemId)   || 0)
    const current_qty    = Number(item.current_on_hand || 0)
    const actual_qty     = baseline_qty + received_qty - wasted_qty - current_qty
    const theoretical_qty = Number(theoreticalByItemId.get(itemId)?.qty || 0)
    const variance_qty   = actual_qty - theoretical_qty
    const variance_pct   = theoretical_qty > 0
      ? variance_qty / theoretical_qty
      : (actual_qty > 0 ? Infinity : 0)
    const unit_cost = unitCostForItem(item)
    const theoretical_cost = theoretical_qty * unit_cost
    const actual_cost      = actual_qty      * unit_cost
    const variance_cost    = variance_qty    * unit_cost

    rows.push({
      item_id:  itemId,
      item_name: item.name,
      unit:      item.unit,
      pack_unit: item.pack_unit,
      pack_size: Number(item.pack_size || 1),
      theoretical_qty,
      actual_qty,
      variance_qty,
      variance_pct,
      theoretical_cost,
      actual_cost,
      variance_cost,
      received_qty,
      wasted_qty,
      baseline_qty,
      current_qty,
      has_baseline: baselineByItemId.has(itemId),
      contributing_recipes:
        theoreticalByItemId.get(itemId)?.contributing_recipes || [],
    })
  }

  // Sort by abs(variance_cost) desc — biggest dollar discrepancies
  // surface first. (Variance_pct sort is too noisy when theoretical
  // is tiny: a 0.1 vs 0.05 swing reads as 100% but is meaningless.)
  rows.sort((a, b) => Math.abs(b.variance_cost) - Math.abs(a.variance_cost))

  // Status flags --------------------------------------------------
  let status = 'ready'
  if (baselineByItemId.size === 0) status = 'no_baseline'
  else if (recipes.filter((r) => r.item_id).length === 0) status = 'no_recipes'
  else if (item_sales.length === 0) status = 'no_sales'

  return {
    status,
    period: { start: period_start, end: period_end },
    rows,
    unlinked_recipe_count,
    unmatched_sales_count,
  }
}

// ---------- Color tier for a variance percentage --------------
// Centralized so the screen UI agrees with any future Daily Huddle
// "biggest variance today" tile. Returns a Tailwind palette name
// the UI can map to bg/text classes.
export function varianceTier(variancePct) {
  const v = Math.abs(variancePct)
  if (!isFinite(v))  return 'red'
  if (v <= 0.10)     return 'green'   // ≤ 10% — within normal portioning drift
  if (v <= 0.25)     return 'amber'   // 10–25% — monitor
  if (v <= 0.50)     return 'red'     // 25–50% — investigate
  return 'deep-red'                    // > 50% — likely shrinkage/process error
}
